// src/components/auth/AuthGuard.tsx
import { useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useAuth } from '@/contexts/AuthContext';

interface AuthGuardProps {
    children: React.ReactNode;
    requiredRole?: 'candidate' | 'employer' | 'admin';
    redirectTo?: string;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
                                                        children,
                                                        requiredRole,
                                                        redirectTo = '/'
                                                    }) => {
    const { isAuthenticated, user, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        // If authentication is still loading, wait
        if (isLoading) return;

        // If not authenticated, redirect to login
        if (!isAuthenticated) {
            navigate(redirectTo);
            return;
        }

        // If role is required and user doesn't have it, redirect
        if (requiredRole && user?.role !== requiredRole) {
            navigate('/');
        }
    }, [isAuthenticated, isLoading, user, requiredRole, navigate, redirectTo]);

    // Show nothing while loading
    if (isLoading) {
        return null; // Or a loading spinner
    }

    // If not authenticated or doesn't have required role, don't render children
    if (!isAuthenticated || (requiredRole && user?.role !== requiredRole)) {
        return null;
    }

    // If authenticated and has required role, render children
    return <>{children}</>;
};